import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { AbstractControl } from "@angular/forms";
import { Subject } from "rxjs";
import { finalize, takeUntil } from "rxjs/operators";
import { GeminiChatService } from "../../services/gemini-chat.service";
import { SharedService } from "../../services/shared.service";
import { SpeechRecognitionService, SpeechTranscriptUpdate } from "../../services/speech-recognition.service";

@Component({
	selector: "app-comment-ai-assist-toolbar",
	templateUrl: "./comment-ai-assist-toolbar.component.html",
	styleUrls: ["./comment-ai-assist-toolbar.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class CommentAiAssistToolbarComponent implements OnInit, OnDestroy {
	@Input({ required: true }) commentControl!: AbstractControl;
	@Input() disabled = false;
	@Output() replace = new EventEmitter<void>();

	isOnline = true;
	isRephrasing = false;
	isListening = false;
	hintText = "Help me write..";
	speechSupported = false;

	private committedValue = "";
	private pendingInterim = "";
	private readonly destroy$ = new Subject<void>();

	constructor(
		private readonly geminiChatService: GeminiChatService,
		private readonly speechRecognitionService: SpeechRecognitionService,
		private readonly sharedService: SharedService,
	) {}

	ngOnInit(): void {
		this.isOnline = this.sharedService.getIsOnline();
		this.speechSupported = this.speechRecognitionService.isSupported();
		this.updateIdleHint();

		this.sharedService
			.getIsStatusOnline()
			.pipe(takeUntil(this.destroy$))
			.subscribe((online) => {
				this.isOnline = online;
				if (!online) {
					this.stopListening();
					this.isRephrasing = false;
				}
				this.updateIdleHint();
			});

		this.speechRecognitionService.transcript$.pipe(takeUntil(this.destroy$)).subscribe((update) => {
			this.applyTranscriptUpdate(update);
		});

		this.speechRecognitionService.error$.pipe(takeUntil(this.destroy$)).subscribe((message) => {
			this.hintText = message;
		});

		this.speechRecognitionService.end$.pipe(takeUntil(this.destroy$)).subscribe(() => {
			this.isListening = false;
			this.clearPendingInterim();
			this.updateIdleHint();
		});
	}

	ngOnDestroy(): void {
		this.stopListening();
		this.destroy$.next();
		this.destroy$.complete();
	}

	get aiAssistAvailable(): boolean {
		return this.isOnline && !this.disabled;
	}

	get showMic(): boolean {
		return this.speechSupported && !this.disabled;
	}

	get canRephrase(): boolean {
		return (
			this.aiAssistAvailable &&
			!this.isRephrasing &&
			!this.isListening &&
			!!String(this.commentControl.value ?? "").trim()
		);
	}

	get canToggleListen(): boolean {
		return this.showMic && this.isOnline && !this.isRephrasing;
	}

	get canReplace(): boolean {
		return !this.disabled && !!String(this.commentControl.value ?? "").trim();
	}

	get micAriaLabel(): string {
		return this.isListening ? "Stop dictation" : "Tap to dictate comment";
	}

	onRephrase(): void {
		if (!this.canRephrase) {
			return;
		}

		const text = String(this.commentControl.value ?? "").trim();
		this.isRephrasing = true;
		this.hintText = "Rephrasing…";

		this.geminiChatService
			.rephrase(text)
			.pipe(
				finalize(() => {
					this.isRephrasing = false;
					this.updateIdleHint();
				}),
				takeUntil(this.destroy$),
			)
			.subscribe({
				next: (response) => {
					if (response.text) {
						this.commentControl.setValue(response.text);
						this.commentControl.markAsDirty();
					}
				},
				error: (error) => {
					this.hintText = this.resolveErrorMessage(error);
				},
			});
	}

	onMicToggle(): void {
		if (!this.canToggleListen) {
			return;
		}

		if (this.isListening) {
			this.stopListening();
			this.updateIdleHint();
			return;
		}

		this.committedValue = String(this.commentControl.value ?? "");
		this.pendingInterim = "";
		this.isListening = true;
		this.hintText = "Listening… Tap to stop";
		this.speechRecognitionService.startListening();
	}

	onReplace(): void {
		if (!this.canReplace) {
			return;
		}

		this.replace.emit();
	}

	private applyTranscriptUpdate(update: SpeechTranscriptUpdate): void {
		if (!this.isListening) {
			return;
		}

		if (update.final) {
			this.committedValue = this.speechRecognitionService.appendTranscript(this.committedValue, update.final);
			this.pendingInterim = "";
		}

		if (update.interim !== undefined) {
			this.pendingInterim = update.interim;
		}

		const display = this.pendingInterim
			? this.speechRecognitionService.appendTranscript(this.committedValue, this.pendingInterim)
			: this.committedValue;

		this.commentControl.setValue(display);
		this.commentControl.markAsDirty();
	}

	private clearPendingInterim(): void {
		if (!this.pendingInterim) {
			this.pendingInterim = "";
			return;
		}

		this.pendingInterim = "";
		this.commentControl.setValue(this.committedValue);
	}

	private stopListening(): void {
		if (!this.isListening) {
			return;
		}

		this.speechRecognitionService.stopListening();
		this.isListening = false;
		this.clearPendingInterim();
	}

	private updateIdleHint(): void {
		if (this.isRephrasing) {
			return;
		}

		if (this.isListening) {
			this.hintText = "Listening… Tap to stop";
			return;
		}

		if (!this.isOnline) {
			this.hintText = "AI assist unavailable offline";
			return;
		}

		this.hintText = "Help me write..";
	}

	private resolveErrorMessage(error: unknown): string {
		if (typeof error === "string") {
			return error;
		}

		if (error && typeof error === "object") {
			const err = error as { message?: string | string[]; error?: string };
			if (Array.isArray(err.message)) {
				return err.message.join(", ");
			}
			if (err.message) {
				return err.message;
			}
			if (err.error) {
				return err.error;
			}
		}

		return "Failed to rephrase comment. Please try again.";
	}
}
